import React from 'react'
import PrivateRoute from './PrivateRoute'
import { MenuLinks } from '@/constants/menu'
import type { RoutesProps } from './utils'

// Static imports (orbit)
import ManagePlatformLookUps from '@/pages/orbit/manage-platform-lookups'
import ManageSubscriptionPlans from '@/pages/orbit/manage-subscription-plans'
import SubscriptionPlanAdminGuard from '@/components/guards/SubscriptionPlanAdminGuard'
import SupportInquiryGuard from '@/components/guards/SupportInquiryGuard'
import ManagePromoCodes from '@/pages/orbit/manage-promo-codes'
import PromoCodeAdminGuard from '@/components/guards/PromoCodeAdminGuard'
import CampaignGuard from '@/components/guards/CampaignGuard'
import SqlDbdInstallerAdminGuard from '@/components/guards/SqlDbdInstallerAdminGuard'
import ManageCustomPricing from '@/pages/orbit/manage-custom-pricing'
import CustomPricingAdminGuard from '@/components/guards/CustomPricingAdminGuard'
import ManageCustomPlanRequests from '@/pages/orbit/manage-custom-plan-requests'
import CustomPlanRequestGuard from '@/components/guards/CustomPlanRequestGuard'
import SubscriptionSales from '@/pages/orbit/subscription-sales'
import ManageTransactions from '@/pages/orbit/manage-transactions'

// Lazy imports (orbit - auth-protected only)
const ManageUsers = React.lazy(() => import('@/pages/orbit/manage-users'))
const ManageRoles = React.lazy(() => import('@/pages/orbit/manage-roles'))
const ManageProfile = React.lazy(() => import('@/pages/orbit/manage-profile'))
const TwoFactorAuthenticationSettings = React.lazy(() => import('@/pages/orbit/two-factor-authentication'))
const ManageLookUps = React.lazy(() => import('@/pages/orbit/manage-lookups'))
const ManageLookUpCodeValues = React.lazy(() => import('@/pages/orbit/manage-lookup-values'))
const ManagePlatformLookUpsValues = React.lazy(() => import('@/pages/orbit/manage-platform-lookup-values'))
const ManageEmailLog = React.lazy(() => import('@/pages/orbit/manage-email-log'))
const ManageSettings = React.lazy(() => import('@/pages/orbit/manage-settings'))
const ManageEmailTemplate = React.lazy(() => import('@/pages/orbit/manage-email-templates'))
const EditTenant = React.lazy(() => import('@/pages/orbit/manage-tenants/Components/EditTenantLandingPage'))
const ManageTenants = React.lazy(() => import('@/pages/orbit/manage-tenants'))
const CustomerDetail = React.lazy(() => import('@/pages/orbit/customer-detail'))
const MySubscriptions = React.lazy(() => import('@/pages/orbit/my-subscriptions'))
const MyCustomPlanRequests = React.lazy(() => import('@/pages/orbit/my-custom-plan-requests'))
const OfflineGrant = React.lazy(() => import('@/pages/orbit/offline-grant'))
const PaymentHistory = React.lazy(() => import('@/pages/orbit/payment-history'))
const PaymentMethods = React.lazy(() => import('@/pages/orbit/payment-methods'))
const NotificationsPage = React.lazy(() => import('@/pages/orbit/notifications'))
const HelpCenterPage = React.lazy(() => import('@/pages/orbit/help-center'))
const HelpSupportPage = React.lazy(() => import('@/pages/orbit/help-support'))
const ReferralPage = React.lazy(() => import('@/pages/orbit/referral'))
const ManageLanguage = React.lazy(() => import('@/pages/orbit/manage-language'))
const ManageLanguageLocalizations = React.lazy(() => import('@/pages/orbit/manage-language/Components/ViewLocalizationPage'))
const ViewSubMenus = React.lazy(() => import('@/pages/orbit/view-sub-menus'))
const BuySubscriptionPlan = React.lazy(() => import('@/pages/orbit/buy-subscription-plan/Index'))
const PaymentConfirmation = React.lazy(() => import('@/pages/orbit/buy-subscription-plan/Components/payment-confirmation'))
const ManageSqlDbdInstaller = React.lazy(() => import('@/pages/orbit/manage-sqldbd-installer'))
const ManageOrgPermissions = React.lazy(() => import('@/pages/orbit/manage-org-permissions'))
const CampaignsPage = React.lazy(() => import('@/pages/orbit/campaigns'))

const orbitAdministratorRoutes: RoutesProps = {
	path: '/administrator',
	name: 'Administrator',
	icon: 'home',
	header: 'Navigation',
	children: [
		{
			path: MenuLinks.ManageUsers,
			name: 'manage_users',
			element: <ManageUsers />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageRoles,
			name: 'manage_roles',
			element: <ManageRoles />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageLookUps,
			name: 'manage_lookups',
			element: <ManageLookUps />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageLookUpValues,
			name: 'manage_lookups',
			element: <ManageLookUpCodeValues />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManagePlatformLookUps,
			name: 'manage_platform_lookups',
			element: <ManagePlatformLookUps />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageSubscriptionPlans,
			name: 'manage_subscription_plans',
			element: (
				<SubscriptionPlanAdminGuard>
					<ManageSubscriptionPlans />
				</SubscriptionPlanAdminGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManagePromoCodes,
			name: 'manage_promo_codes',
			element: (
				<PromoCodeAdminGuard>
					<ManagePromoCodes />
				</PromoCodeAdminGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageCustomPricing,
			name: 'manage_custom_pricing',
			element: (
				<CustomPricingAdminGuard>
					<ManageCustomPricing />
				</CustomPricingAdminGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageCustomPlanRequests,
			name: 'manage_custom_plan_requests',
			element: (
				<CustomPlanRequestGuard>
					<ManageCustomPlanRequests />
				</CustomPlanRequestGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.SubscriptionSales,
			name: 'subscription_sales',
			element: <SubscriptionSales />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.Transactions,
			name: 'transactions',
			element: <ManageTransactions />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.BuySubscriptionPlan,
			name: 'buy_subscription_plan',
			element: <BuySubscriptionPlan embeddedInDashboard />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.PaymentConfirmation,
			name: 'payment_confirmation',
			element: <PaymentConfirmation />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManagePlatformLookUpValues,
			name: 'manage_platform_look_ups',
			element: <ManagePlatformLookUpsValues />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageEmailLog,
			name: 'manage_emaillog',
			element: <ManageEmailLog />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageSettings,
			name: 'manage_settings',
			element: <ManageSettings />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageEmailTemplate,
			name: 'manage_emailTemplates',
			element: <ManageEmailTemplate />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageLanguage,
			name: 'manage_countryLocalization',
			element: <ManageLanguage />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageLanguageLocalizations,
			name: 'manage_countryLocalization',
			element: <ManageLanguageLocalizations />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageTenants,
			name: 'manage_tenants',
			element: <ManageTenants />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ViewSubMenus,
			name: 'view_sub_menus',
			element: <ViewSubMenus />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.EditTenantDetails,
			name: 'edit_tenant_details',
			element: <EditTenant />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.CustomerDetail,
			name: 'customer_detail',
			element: <CustomerDetail />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.MySubscriptions,
			name: 'my_subscriptions',
			element: <MySubscriptions />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.Campaigns,
			name: 'campaigns',
			element: (
				<CampaignGuard>
					<CampaignsPage />
				</CampaignGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.MyCustomPlanRequests,
			name: 'my_custom_plan_requests',
			element: <MyCustomPlanRequests />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.PaymentHistory,
			name: 'payment_history',
			element: <PaymentHistory />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.PaymentMethods,
			name: 'payment_methods',
			element: <PaymentMethods />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.OfflineGrant,
			name: 'offline_grant',
			element: <OfflineGrant />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.Notifications,
			name: 'notifications',
			element: <NotificationsPage />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.HelpCenter,
			name: 'help_center',
			element: <HelpCenterPage />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.HelpSupport,
			name: 'help_support',
			element: (
				<SupportInquiryGuard>
					<HelpSupportPage />
				</SupportInquiryGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.Referral,
			name: 'referral',
			element: <ReferralPage />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageSqlDbdInstaller,
			name: 'manage_sqldbd_installer',
			element: (
				<SqlDbdInstallerAdminGuard>
					<ManageSqlDbdInstaller />
				</SqlDbdInstallerAdminGuard>
			),
			route: PrivateRoute,
		},
		{
			path: MenuLinks.ManageOrgPermissions,
			name: 'manage_org_permissions',
			element: (
				// Same Root-only gate as the SQLDBD installer page — reusing it rather than adding a
				// near-identical guard component for a single extra route.
				<SqlDbdInstallerAdminGuard>
					<ManageOrgPermissions />
				</SqlDbdInstallerAdminGuard>
			),
			route: PrivateRoute,
		},
	],
}

const orbitTopMenuRoutes: RoutesProps = {
	path: '/topmenu',
	name: 'TopMenu',
	icon: 'home',
	header: 'Navigation',
	children: [
		{
			path: MenuLinks.ManageProfile,
			name: 'manage_profile',
			element: <ManageProfile />,
			route: PrivateRoute,
		},
		{
			path: MenuLinks.UserTwoFactorAuthentication,
			name: 'two_factor_authentication',
			element: <TwoFactorAuthenticationSettings />,
			route: PrivateRoute,
		},
	],
}

export const orbitRoutes: RoutesProps[] = [orbitAdministratorRoutes, orbitTopMenuRoutes]
